/**
 * The builder's Theme panel, as in AgencyAnalytics: the default library
 * (Classic, Elegant, Playful, Bold) and the agency's saved themes, each
 * applied with one click; Customize to change background, fonts and colours;
 * and Save custom theme to keep the current look for other reports.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { PRESET_THEMES, isThemed, sameTheme, type ReportTheme } from './report-theme.ts';
import { ThemeCustomize } from './ThemeCustomize.tsx';
import { themesApi, type SavedTheme } from './themes-api.ts';

const SWATCH_DOTS = 4;

function ThemeCard(props: {
  label: string;
  theme: ReportTheme;
  active: boolean;
  onApply: () => void;
}): ReactNode {
  const { theme } = props;
  return (
    <button type="button" className="rth-card" aria-pressed={props.active} onClick={props.onApply}>
      <span
        className="rth-swatch"
        style={{ background: theme.background_color ?? '#ffffff', color: theme.text_color }}
        aria-hidden="true"
      >
        <span style={{ background: theme.card_color, borderRadius: 4, padding: '0.3rem' }}>
          <strong style={{ fontFamily: theme.title_font }}>Aa</strong>{' '}
          <small style={{ fontFamily: theme.body_font }}>{theme.body_font ?? 'Inter'}</small>
        </span>
        <span className="rth-dots">
          {(theme.series ?? []).slice(0, SWATCH_DOTS).map((colour) => (
            <span key={colour} style={{ background: colour }} />
          ))}
        </span>
      </span>
      {props.label}
    </button>
  );
}

function SaveTheme(props: { onSave: (name: string) => Promise<void> }): ReactNode {
  const [name, setName] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="rth"
      onSubmit={async (event) => {
        event.preventDefault();
        setBusy(true);
        try {
          await props.onSave(name.trim());
          setName('');
          setProblem(null);
        } catch (error) {
          setProblem(error instanceof Error ? error.message : 'The theme could not be saved.');
        } finally {
          setBusy(false);
        }
      }}
    >
      <label className="rs-field">
        Theme name
        <input
          value={name}
          maxLength={60}
          required
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      <button type="submit" className="btn small" disabled={busy || !name.trim()}>
        Save custom theme
      </button>
      {problem && (
        <p className="rh-access-msg rh-access-error" role="alert">
          {problem}
        </p>
      )}
    </form>
  );
}

export function ThemePanel({
  organisationId,
  theme,
  onTheme,
  onClose,
}: {
  organisationId: string;
  theme: ReportTheme;
  onTheme: (theme: ReportTheme) => void;
  onClose: () => void;
}): ReactNode {
  const [customizing, setCustomizing] = useState(false);
  const [saved, setSaved] = useState<SavedTheme[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  useEffect(() => {
    themesApi.list(organisationId).then(setSaved, () => setSaved([]));
  }, [organisationId]);

  return (
    <aside className="rs" aria-label="Theme">
      <header className="rs-head">
        <strong>{customizing ? 'Customize theme' : 'Theme'}</strong>
        <button type="button" className="rb-icon" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </header>
      <div className="rs-body rth">
        {customizing ? (
          <>
            <button
              type="button"
              className="btn small secondary"
              onClick={() => setCustomizing(false)}
            >
              ← All themes
            </button>
            <ThemeCustomize theme={theme} onTheme={onTheme} />
            <SaveTheme
              onSave={async (name) => {
                const kept = await themesApi.save(organisationId, name, theme);
                setSaved((current) =>
                  [...current.filter((item) => item.id !== kept.id), kept].sort((a, b) =>
                    a.name.localeCompare(b.name),
                  ),
                );
                onTheme(kept.theme);
              }}
            />
          </>
        ) : (
          <>
            <button type="button" className="btn small" onClick={() => setCustomizing(true)}>
              Customize current theme
            </button>
            <h3 className="rs-subhead">Default library</h3>
            <div className="rth-cards">
              <ThemeCard
                label="SynAnalytics"
                theme={{}}
                active={!isThemed(theme)}
                onApply={() => onTheme({})}
              />
              {PRESET_THEMES.map((preset) => (
                <ThemeCard
                  key={preset.preset}
                  label={preset.name ?? ''}
                  theme={preset}
                  active={sameTheme(theme, preset)}
                  onApply={() => onTheme(preset)}
                />
              ))}
            </div>
            <h3 className="rs-subhead">Your themes</h3>
            {problem && (
              <p className="rh-access-msg rh-access-error" role="alert">
                {problem}
              </p>
            )}
            {saved.length === 0 && (
              <p className="rs-explain">Customize a theme, then save it to reuse it here.</p>
            )}
            <ul className="rth-saved">
              {saved.map((item) => (
                <li key={item.id} className="rth-row">
                  <ThemeCard
                    label={item.name}
                    theme={item.theme}
                    active={sameTheme(theme, item.theme)}
                    onApply={() => onTheme(item.theme)}
                  />
                  <button
                    type="button"
                    className="rb-icon"
                    aria-label={`Remove ${item.name}`}
                    onClick={() =>
                      themesApi.remove(organisationId, item.id).then(
                        () => setSaved((current) => current.filter((kept) => kept.id !== item.id)),
                        () => setProblem(`“${item.name}” could not be removed. Try again.`),
                      )
                    }
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </aside>
  );
}
